import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Send, Loader2, MessageSquare, Volume2, VolumeX, Trash2 } from "lucide-react";
import { useAuth, getAuthHeaders } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { useUnreadMessages } from "../context/UnreadMessagesContext";
import { CandidateAvatar } from "../components/CandidateAvatar";
import { CompanyAvatar } from "../components/CompanyAvatar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Single Mensajes screen shared by candidates and employers — same list+thread
// UI, only the "other party" label/avatar changes per role. On mobile the list
// and the open thread are two full-screen states (toggled by selection); on
// desktop they sit side by side. Realtime (new messages, typing, deletes) is
// a single shared subscription from UnreadMessagesContext's WebSocket — this
// page never opens its own socket.
export default function MessagesPage() {
  const { user, loading: authLoading } = useAuth();
  const { t } = useLanguage();
  const { refetchUnread, soundEnabled, setSoundEnabled, subscribeToMessages, sendTyping, setActiveConversation } = useUnreadMessages();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(searchParams.get("c") || null);
  const [messages, setMessages] = useState([]);
  const [threadLoading, setThreadLoading] = useState(false);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [otherTyping, setOtherTyping] = useState(false);
  const bottomRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const isNearBottomRef = useRef(true);
  const justOpenedRef = useRef(false);
  const typingHideTimerRef = useRef(null);
  const typingStopTimerRef = useRef(null);
  const isTypingSentRef = useRef(false);
  const activeIdRef = useRef(activeId);
  activeIdRef.current = activeId;

  const isEmployer = user?.role === "employer";

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/auth"); return; }
    const load = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/conversations`, { headers: getAuthHeaders() });
        setConversations(res.data?.conversations || []);
      } catch { /* non-critical — empty state covers this */ }
      finally { setLoading(false); }
    };
    load();
  }, [user, authLoading, navigate]);

  const activeConvo = useMemo(() => conversations.find((c) => c.conversation_id === activeId), [conversations, activeId]);

  const openThread = async (conversationId) => {
    if (isTypingSentRef.current && activeIdRef.current) {
      isTypingSentRef.current = false;
      if (typingStopTimerRef.current) clearTimeout(typingStopTimerRef.current);
      sendTyping(activeIdRef.current, false);
    }
    setActiveId(conversationId);
    setActiveConversation(conversationId);
    setSearchParams({ c: conversationId }, { replace: true });
    setThreadLoading(true);
    justOpenedRef.current = true;
    try {
      const res = await axios.get(`${API}/conversations/${conversationId}/messages`, { headers: getAuthHeaders() });
      setMessages(res.data?.messages || []);
      setConversations((prev) => prev.map((c) => (c.conversation_id === conversationId ? { ...c, unread_count: 0 } : c)));
      refetchUnread();
    } catch { setMessages([]); }
    finally { setThreadLoading(false); }
  };

  useEffect(() => {
    if (activeId) openThread(activeId);
    // Leaving the Mensajes screen entirely (route change/unmount) must clear
    // this tab's presence — otherwise the backend would keep thinking this
    // tab is still looking at that conversation and wrongly skip a push.
    return () => {
      setActiveConversation(null);
      if (isTypingSentRef.current && activeIdRef.current) sendTyping(activeIdRef.current, false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Scope the scroll to the messages container itself instead of
    // bottomRef.scrollIntoView() — scrollIntoView can bubble up and move the
    // whole window (not just this panel) while layout is still settling
    // right after navigation, which is what caused Mensajes to sometimes
    // load scrolled to the bottom of the page. Opening a thread always jumps
    // to the latest message (composer stays visible); once already reading,
    // a live message only auto-scrolls if the user was already at/near the
    // bottom — reading older messages is never interrupted.
    const el = messagesContainerRef.current;
    if (!el) return;
    if (justOpenedRef.current || isNearBottomRef.current) {
      el.scrollTop = el.scrollHeight;
    }
    justOpenedRef.current = false;
  }, [messages]);

  const handleThreadScroll = () => {
    const el = messagesContainerRef.current;
    if (!el) return;
    isNearBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
  };

  // ── Realtime: single shared WebSocket subscription (UnreadMessagesContext) ──
  useEffect(() => {
    const unsubscribe = subscribeToMessages((data) => {
      if (data.type === "new_message") {
        if (data.conversation_id === activeIdRef.current) {
          setMessages((prev) => (prev.some((m) => m.message_id === data.message.message_id) ? prev : [...prev, data.message]));
          axios.post(`${API}/conversations/${data.conversation_id}/read`, {}, { headers: getAuthHeaders() }).then(refetchUnread).catch(() => {});
          setOtherTyping(false);
        }
        setConversations((prev) => {
          const idx = prev.findIndex((c) => c.conversation_id === data.conversation_id);
          if (idx === -1) {
            axios.get(`${API}/conversations`, { headers: getAuthHeaders() }).then((res) => setConversations(res.data?.conversations || [])).catch(() => {});
            return prev;
          }
          const updatedConvo = {
            ...prev[idx],
            last_message: data.message.body,
            last_message_at: data.message.created_at,
            unread_count: data.conversation_id === activeIdRef.current ? 0 : (prev[idx].unread_count || 0) + 1,
          };
          return [updatedConvo, ...prev.filter((_, i) => i !== idx)];
        });
      } else if (data.type === "typing" && data.conversation_id === activeIdRef.current) {
        if (typingHideTimerRef.current) clearTimeout(typingHideTimerRef.current);
        setOtherTyping(!!data.is_typing);
        if (data.is_typing) typingHideTimerRef.current = setTimeout(() => setOtherTyping(false), 4000);
      } else if (data.type === "conversation_deleted") {
        setConversations((prev) => prev.filter((c) => c.conversation_id !== data.conversation_id));
        if (data.conversation_id === activeIdRef.current) closeThread();
      }
    });
    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscribeToMessages, refetchUnread]);

  const closeThread = () => {
    if (isTypingSentRef.current && activeId) {
      isTypingSentRef.current = false;
      if (typingStopTimerRef.current) clearTimeout(typingStopTimerRef.current);
      sendTyping(activeId, false);
    }
    setActiveId(null);
    setActiveConversation(null);
    setMessages([]);
    setOtherTyping(false);
    setSearchParams({}, { replace: true });
  };

  const handleDraftChange = (e) => {
    const value = e.target.value;
    setDraft(value);
    if (!activeId) return;
    if (!value.trim()) {
      if (typingStopTimerRef.current) clearTimeout(typingStopTimerRef.current);
      if (isTypingSentRef.current) { isTypingSentRef.current = false; sendTyping(activeId, false); }
      return;
    }
    if (!isTypingSentRef.current) {
      isTypingSentRef.current = true;
      sendTyping(activeId, true);
    }
    if (typingStopTimerRef.current) clearTimeout(typingStopTimerRef.current);
    typingStopTimerRef.current = setTimeout(() => {
      isTypingSentRef.current = false;
      sendTyping(activeId, false);
    }, 2000);
  };

  const sendMessage = async () => {
    if (!draft.trim() || !activeId) return;
    setSending(true);
    const body = draft.trim();
    setDraft("");
    if (typingStopTimerRef.current) clearTimeout(typingStopTimerRef.current);
    if (isTypingSentRef.current) { isTypingSentRef.current = false; sendTyping(activeId, false); }
    try {
      const res = await axios.post(`${API}/conversations/${activeId}/messages`, { body }, { headers: getAuthHeaders() });
      isNearBottomRef.current = true;
      setMessages((prev) => [...prev, res.data]);
      setConversations((prev) => prev.map((c) => (c.conversation_id === activeId ? { ...c, last_message: body, last_message_at: res.data.created_at } : c)));
    } catch { setDraft(body); }
    finally { setSending(false); }
  };

  const deleteConversation = async (conversationId, e) => {
    e?.stopPropagation();
    if (!window.confirm(t("messages.deleteConversationConfirm"))) return;
    try {
      await axios.delete(`${API}/conversations/${conversationId}`, { headers: getAuthHeaders() });
      setConversations((prev) => prev.filter((c) => c.conversation_id !== conversationId));
      if (activeId === conversationId) closeThread();
    } catch { /* transient failure — row stays, user can retry */ }
  };

  const otherParty = (c) => (isEmployer
    ? { name: c.candidate_name, picture: c.candidate_picture, isCompany: false }
    : { name: c.company_name, picture: c.company_logo, isCompany: true });

  const timeLabel = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    const diffDays = Math.floor((Date.now() - d) / 86400000);
    if (diffDays === 0) return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    if (diffDays < 7) return d.toLocaleDateString([], { weekday: "short" });
    return d.toLocaleDateString();
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-[#028CF8] animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="messages-page" className="messages-page-shell bg-slate-50 overflow-hidden">
      <div className="container mx-auto px-0 sm:px-4 md:px-6 lg:px-8 py-0 sm:py-6 h-full">
        <div className="bg-white sm:rounded-2xl sm:border sm:border-slate-200 sm:shadow-sm overflow-hidden h-full">
          <div className="grid grid-cols-1 md:grid-cols-[320px_1fr] grid-rows-1 h-full">

            {/* ── Conversation list ── */}
            <div className={`border-r border-slate-100 flex flex-col h-full min-h-0 ${activeId ? "hidden md:flex" : "flex"}`}>
              <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between gap-2 flex-shrink-0">
                <h1 className="font-poppins text-lg font-bold text-slate-900">{t("messages.title")}</h1>
                <button
                  data-testid="message-sound-toggle"
                  onClick={() => setSoundEnabled(!soundEnabled)}
                  title={soundEnabled ? t("messages.soundOnTooltip") : t("messages.soundOffTooltip")}
                  className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors flex-shrink-0"
                >
                  {soundEnabled ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
                </button>
              </div>
              <div className="flex-1 min-h-0 overflow-y-auto">
              {conversations.length === 0 ? (
                <div className="p-10 text-center" data-testid="messages-empty-state">
                  <MessageSquare className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-sm font-semibold text-slate-700 mb-1">{t("messages.emptyTitle")}</p>
                  <p className="text-xs text-slate-500">{t("messages.emptyDesc")}</p>
                </div>
              ) : (
                <div data-testid="messages-list">
                  {conversations.map((c) => {
                    const other = otherParty(c);
                    const unread = c.unread_count > 0;
                    return (
                      <div
                        key={c.conversation_id}
                        role="button"
                        tabIndex={0}
                        onClick={() => openThread(c.conversation_id)}
                        onKeyDown={(e) => { if (e.key === "Enter") openThread(c.conversation_id); }}
                        data-testid={`conversation-row-${c.conversation_id}`}
                        className={`group w-full flex items-center gap-3 px-5 py-3.5 text-left border-b border-slate-50 [@media(hover:hover)]:hover:bg-slate-50 transition-colors cursor-pointer ${activeId === c.conversation_id ? "bg-[#028CF8]/5" : ""}`}
                      >
                        {other.isCompany
                          ? <CompanyAvatar name={other.name} logo={other.picture} size={44} rounded={12} />
                          : <CandidateAvatar name={other.name} picture={other.picture} size={44} rounded="xl" />}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <p className={`text-sm truncate ${unread ? "font-bold text-slate-900" : "font-semibold text-slate-800"}`}>{other.name}</p>
                            <span className="text-[11px] text-slate-400 flex-shrink-0">{timeLabel(c.last_message_at)}</span>
                          </div>
                          <p className={`text-xs truncate ${unread ? "text-slate-700 font-semibold" : "text-slate-500"}`}>{c.last_message || "—"}</p>
                        </div>
                        {unread && <span data-testid={`conversation-unread-${c.conversation_id}`} className="h-2.5 w-2.5 rounded-full bg-[#028CF8] flex-shrink-0" />}
                        <button
                          data-testid={`conversation-delete-${c.conversation_id}`}
                          onClick={(e) => deleteConversation(c.conversation_id, e)}
                          title={t("messages.deleteConversationTooltip")}
                          className="flex-shrink-0 p-1.5 rounded-lg text-slate-300 hover:text-red-500 hover:bg-red-50 transition-colors opacity-0 [@media(hover:hover)]:group-hover:opacity-100 focus:opacity-100"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
              </div>
            </div>

            {/* ── Thread ── */}
            <div className={`flex flex-col h-full min-h-0 min-w-0 w-full max-w-full ${activeId ? "" : "hidden md:flex"}`} data-testid="message-thread">
              {!activeConvo ? (
                <div className="flex-1 flex items-center justify-center text-sm text-slate-400">
                  {t("messages.selectConversation")}
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-100 flex-shrink-0">
                    <button onClick={closeThread} data-testid="message-thread-back" className="md:hidden p-1.5 -ml-1.5 rounded-lg text-slate-400 hover:bg-slate-100">
                      <ArrowLeft className="h-5 w-5" />
                    </button>
                    {(() => {
                      const other = otherParty(activeConvo);
                      return other.isCompany
                        ? <CompanyAvatar name={other.name} logo={other.picture} size={38} rounded={10} />
                        : <CandidateAvatar name={other.name} picture={other.picture} size={38} rounded="xl" />;
                    })()}
                    <div className="flex-1 min-w-0">
                      <p className="font-poppins font-bold text-sm text-slate-900 truncate">{otherParty(activeConvo).name}</p>
                    </div>
                    <button
                      data-testid="message-thread-delete"
                      onClick={(e) => deleteConversation(activeId, e)}
                      title={t("messages.deleteConversationTooltip")}
                      className="flex-shrink-0 p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>

                  <div ref={messagesContainerRef} onScroll={handleThreadScroll} className="flex-1 min-h-0 overflow-y-auto px-4 sm:px-6 py-4 space-y-3">
                    {threadLoading ? (
                      <div className="flex items-center justify-center py-10"><Loader2 className="h-5 w-5 text-[#028CF8] animate-spin" /></div>
                    ) : (
                      messages.map((m) => {
                        const own = m.sender_id === user?.user_id;
                        return (
                          <div key={m.message_id} data-testid={`message-bubble-${m.message_id}`} className={`flex ${own ? "justify-end" : "justify-start"}`}>
                            <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm ${own ? "bg-[#028CF8] text-white" : "bg-slate-100 text-slate-800"}`}>
                              <p className="whitespace-pre-wrap break-words">{m.body}</p>
                              <p className={`text-[10px] mt-1 ${own ? "text-white/70" : "text-slate-400"}`}>{timeLabel(m.created_at)}</p>
                            </div>
                          </div>
                        );
                      })
                    )}
                    <div ref={bottomRef} />
                  </div>

                  {otherTyping && (
                    <div data-testid="typing-indicator" className="px-4 sm:px-6 pb-1 flex-shrink-0 text-xs text-[#028CF8] font-medium">
                      {t("messages.typingIndicator")}
                    </div>
                  )}

                  <div className="flex items-center gap-2 px-4 sm:px-6 py-3 border-t border-slate-100 flex-shrink-0 w-full max-w-full box-border" style={{ paddingBottom: "calc(12px + env(safe-area-inset-bottom))" }}>
                    <input
                      data-testid="message-input"
                      value={draft}
                      onChange={handleDraftChange}
                      onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                      placeholder={t("messages.messagePlaceholder")}
                      className="flex-1 min-w-0 w-full h-11 rounded-xl border border-slate-200 px-4 text-sm focus:outline-none focus:ring-2 focus:ring-[#028CF8]/20 focus:border-[#028CF8]"
                    />
                    <button
                      onClick={sendMessage}
                      disabled={sending || !draft.trim()}
                      data-testid="message-send-button"
                      className="flex items-center gap-1.5 h-11 px-3 sm:px-4 rounded-xl bg-[#028CF8] text-white text-sm font-semibold disabled:opacity-50 flex-shrink-0"
                    >
                      {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                      <span className="hidden sm:inline">{t("messages.send")}</span>
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
